import { syncUserEmployeeInStorage } from "@components/utils/authEmployeeContext";

export const IMPERSONATION_SESSION_KEY = "impersonationSession";
/** Stash for in-account view-as/preview so Exit can restore the admin (ER-10317). */
export const VIEW_AS_ADMIN_RESTORE_KEY = "viewAsAdminRestore";
/** Marker so Exit never takes the act-as → admin-app path for in-account sessions. */
export const VIEW_AS_IN_ACCOUNT_MARKER_KEY = "viewAsInAccount";

export const MODE_ACT_AS = "act_as";
export const MODE_VIEW_AS = "view_as";
export const MODE_PREVIEW = "preview";

export const IMPERSONATION_DURATION_MS = 120 * 60 * 1000;
export const VIEW_AS_DURATION_MS = 30 * 60 * 1000;

export const VIEW_AS_EMPLOYEES_RETURN_PATH = "/organization/employees";

/** Access statuses that are surfaced in the banner as a "[status]" flag (rules §7). */
const NON_ACTIVE_TARGET_STATUSES = ["suspended", "blocked", "closed"];

export function canAdminImpersonate() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user.can_impersonate === true || user.is_superuser === true;
  } catch {
    return false;
  }
}

export function getImpersonationSession() {
  try {
    return JSON.parse(localStorage.getItem(IMPERSONATION_SESSION_KEY) || "null");
  } catch {
    return null;
  }
}

export function isImpersonationActive() {
  const session = getImpersonationSession();
  if (!session?.isImpersonating) return false;
  if (session.expiresAt && Date.parse(session.expiresAt) <= Date.now()) return false;
  return true;
}

export function isReadOnlyImpersonationActive() {
  if (!isImpersonationActive()) return false;
  const session = getImpersonationSession();
  return (
    session?.readOnly === true ||
    session?.mode === MODE_VIEW_AS ||
    session?.mode === MODE_PREVIEW
  );
}

export function isPreviewModeActive() {
  return isImpersonationActive() && getImpersonationSession()?.mode === MODE_PREVIEW;
}

export function isViewAsModeActive() {
  return isImpersonationActive() && getImpersonationSession()?.mode === MODE_VIEW_AS;
}

/** True for in-account view-as/preview (not cross-origin act-as). */
export function isInAccountReadOnlySession() {
  if (!isImpersonationActive()) return false;
  const session = getImpersonationSession();
  return (
    session?.readOnly === true ||
    session?.mode === MODE_VIEW_AS ||
    session?.mode === MODE_PREVIEW
  );
}

/**
 * Preview sidebar: ClientLogo + Your Stuff only (no real Access).
 * View-as keeps the impersonated user's real nav from accessPermission (ER-11088).
 * @param {object|null|undefined} sidebarOption
 */
export function filterSidebarOptionForPreview(sidebarOption) {
  if (!sidebarOption || !isPreviewModeActive()) return sidebarOption;
  const tItems = (sidebarOption.tItems || []).filter((item) => {
    if (!item) return false;
    if (item.name === "ClientLogo" || item.name === "Your Stuff") return true;
    const to = item.to || "";
    return typeof to === "string" && to.startsWith("/your-stuff");
  });
  return {
    ...sidebarOption,
    tItems,
    bItems: [],
  };
}

/** Display name for the impersonated target: full name when present, otherwise email. */
function resolveImpersonatedName(user, employee) {
  if (user) {
    const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
    if (fullName) return fullName;
    if (user?.email) return user.email;
  }
  if (employee) {
    const empName = `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim();
    if (empName) return empName;
    if (employee?.email) return employee.email;
  }
  return "";
}

/** Target access status shown in the banner ("suspended"/"blocked"/"closed"); "" when active. */
function resolveTargetStatus(user, explicitStatus) {
  if (explicitStatus && NON_ACTIVE_TARGET_STATUSES.includes(String(explicitStatus).toLowerCase())) {
    return String(explicitStatus).toLowerCase();
  }
  const state = String(user?.state || "").toLowerCase();
  return NON_ACTIVE_TARGET_STATUSES.includes(state) ? state : "";
}

function defaultDurationMs(mode) {
  return mode === MODE_VIEW_AS || mode === MODE_PREVIEW
    ? VIEW_AS_DURATION_MS
    : IMPERSONATION_DURATION_MS;
}

/**
 * Normalize preview synthetic grant so sidebar/route gates recognize Your Stuff.
 * Backend returns `{ your_stuff: true, admin: null }`.
 */
function normalizeAccessPermission(accessPermission, mode, employee) {
  if (!accessPermission) return accessPermission;
  if (mode !== MODE_PREVIEW) return accessPermission;

  return {
    ...accessPermission,
    your_stuff: true,
    your_stuff_permission: true,
    yourstuff_permission: true,
    has_employee: true,
    admin_permission: "none",
    admin: null,
    employee_id: employee?.id ?? accessPermission.employee_id ?? null,
  };
}

/**
 * Snapshot the admin's account-app session before switching to view-as/preview.
 * Act-as (cross-origin) must never stash the admin token here.
 */
export function stashAdminRestoreContext() {
  const snapshot = {
    token: localStorage.getItem("token"),
    user: localStorage.getItem("user"),
    currentAccountID: localStorage.getItem("currentAccountID"),
    accessPermission: localStorage.getItem("accessPermission"),
    currentAccountDetails: localStorage.getItem("currentAccountDetails"),
    referenceCurrency: localStorage.getItem("referenceCurrency"),
  };
  sessionStorage.setItem(VIEW_AS_ADMIN_RESTORE_KEY, JSON.stringify(snapshot));
  sessionStorage.setItem(VIEW_AS_IN_ACCOUNT_MARKER_KEY, "1");
}

export function restoreAdminContextFromStash() {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_ADMIN_RESTORE_KEY);
    if (!raw) return false;
    const snapshot = JSON.parse(raw);
    if (snapshot.token) localStorage.setItem("token", snapshot.token);
    if (snapshot.user) localStorage.setItem("user", snapshot.user);
    if (snapshot.currentAccountID) {
      localStorage.setItem("currentAccountID", snapshot.currentAccountID);
    }
    if (snapshot.accessPermission) {
      localStorage.setItem("accessPermission", snapshot.accessPermission);
    }
    if (snapshot.currentAccountDetails) {
      localStorage.setItem("currentAccountDetails", snapshot.currentAccountDetails);
    }
    if (snapshot.referenceCurrency) {
      localStorage.setItem("referenceCurrency", snapshot.referenceCurrency);
    }
    sessionStorage.removeItem(VIEW_AS_ADMIN_RESTORE_KEY);
    sessionStorage.removeItem(VIEW_AS_IN_ACCOUNT_MARKER_KEY);
    localStorage.removeItem(IMPERSONATION_SESSION_KEY);
    return true;
  } catch {
    return false;
  }
}

export function clearAdminRestoreStash() {
  sessionStorage.removeItem(VIEW_AS_ADMIN_RESTORE_KEY);
  sessionStorage.removeItem(VIEW_AS_IN_ACCOUNT_MARKER_KEY);
}

/** True when Exit must restore in-account (never redirect to admin app). */
export function shouldExitToAccountEmployees(session = getImpersonationSession()) {
  try {
    if (
      typeof sessionStorage !== "undefined" &&
      (sessionStorage.getItem(VIEW_AS_IN_ACCOUNT_MARKER_KEY) === "1" ||
        !!sessionStorage.getItem(VIEW_AS_ADMIN_RESTORE_KEY))
    ) {
      return true;
    }
  } catch {
    /* ignore */
  }
  if (!session) return false;
  if (session.readOnly === true) return true;
  if (session.mode === MODE_VIEW_AS || session.mode === MODE_PREVIEW) return true;
  if (
    typeof session.returnUrl === "string" &&
    session.returnUrl.includes("/organization/employees")
  ) {
    return true;
  }
  return false;
}

/**
 * Re-entrancy + countdown cleanup for Exit (ER-10317).
 * First call wins; later calls (e.g. countdown after restore) are no-ops.
 *
 * @param {{ endingImpersonationSession: boolean, impersonationCountdownTimer: ReturnType<typeof setInterval>|null }} state
 * @returns {{ started: boolean, endingImpersonationSession: boolean, impersonationCountdownTimer: null }}
 */
export function beginEndImpersonationGuard(state) {
  if (state?.endingImpersonationSession) {
    return {
      started: false,
      endingImpersonationSession: true,
      impersonationCountdownTimer: state.impersonationCountdownTimer ?? null,
    };
  }
  if (state?.impersonationCountdownTimer) {
    clearInterval(state.impersonationCountdownTimer);
  }
  return {
    started: true,
    endingImpersonationSession: true,
    impersonationCountdownTimer: null,
  };
}

/**
 * Decide Exit destination: in-account Employees vs admin act-as.
 * @param {object|null|undefined} session
 */
export function resolveImpersonationExitPath(session) {
  const mode = session?.mode;
  const returnUrl = session?.returnUrl || "/accounts";
  const isExplicitActAs =
    mode === MODE_ACT_AS &&
    session?.readOnly !== true &&
    !shouldExitToAccountEmployees(session);
  const isInAccountReadOnly =
    !isExplicitActAs && (!!session || shouldExitToAccountEmployees(session));
  return {
    mode,
    returnUrl,
    isExplicitActAs,
    isInAccountReadOnly,
  };
}

/**
 * Persist the impersonated (target) user's context so the account app boots as that
 * user. For act-as, only the short-lived impersonation token is stored — the admin's
 * own token is never carried into the impersonated session. For in-account
 * view-as/preview, call `stashAdminRestoreContext()` first so Exit can restore.
 */
export function applyImpersonationAccountContext(payload) {
  const {
    token,
    user,
    employee,
    access_permission: accessPermission,
    current_account: currentAccount,
    account_details: accountDetails,
    expires_at: expiresAt,
    impersonation_session_id: impersonationSessionId,
    admin_id: adminId,
    returnUrl,
    mode: payloadMode,
    read_only: readOnly,
    targetStatus: explicitTargetStatus,
  } = payload;

  const mode = payloadMode || MODE_ACT_AS;
  const isReadOnly = readOnly === true || mode === MODE_VIEW_AS || mode === MODE_PREVIEW;
  const accountId = currentAccount?.id ?? accountDetails?.id;
  let normalizedAccess = normalizeAccessPermission(accessPermission, mode, employee);
  if (normalizedAccess) {
    normalizedAccess = {
      ...normalizedAccess,
      employee: normalizedAccess.employee || employee || null,
    };
  } else if (employee) {
    normalizedAccess = { employee };
  }

  // Preview has no real user; keep a minimal user object so the shell still renders.
  const resolvedEmployee = employee || normalizedAccess?.employee || null;
  const resolvedUser =
    user ||
    (resolvedEmployee
      ? {
          id: null,
          first_name: resolvedEmployee.first_name,
          last_name: resolvedEmployee.last_name,
          email: resolvedEmployee.email || "",
          state: "operational",
          employee: resolvedEmployee,
        }
      : null);

  localStorage.setItem("token", token);
  if (currentAccount?.id != null) {
    localStorage.setItem("currentAccountID", String(currentAccount.id));
  }
  if (normalizedAccess) {
    localStorage.setItem("accessPermission", JSON.stringify(normalizedAccess));
  }
  if (accountDetails) {
    localStorage.setItem("currentAccountDetails", JSON.stringify(accountDetails));
  }
  if (currentAccount?.currency) {
    localStorage.setItem("referenceCurrency", currentAccount.currency);
  }

  if (user != null) {
    syncUserEmployeeInStorage({
      user,
      accessPermission: normalizedAccess,
      accountId,
    });
  } else if (resolvedUser) {
    localStorage.setItem("user", JSON.stringify(resolvedUser));
    if (resolvedUser.employee?.id != null) {
      localStorage.setItem("emp_id", String(resolvedUser.employee.id));
    }
  }

  if (isReadOnly) {
    // Belt-and-suspenders with stashAdminRestoreContext — survives mode quirks on DEV.
    try {
      sessionStorage.setItem(VIEW_AS_IN_ACCOUNT_MARKER_KEY, "1");
    } catch {
      /* ignore */
    }
  }

  localStorage.setItem(
    IMPERSONATION_SESSION_KEY,
    JSON.stringify({
      isImpersonating: true,
      mode,
      readOnly: isReadOnly,
      impersonatedUserEmail: resolvedUser?.email || employee?.email || "",
      impersonatedName: resolveImpersonatedName(resolvedUser, employee),
      accountName: currentAccount?.name || accountDetails?.name || "",
      targetStatus: resolveTargetStatus(resolvedUser, explicitTargetStatus),
      expiresAt: expiresAt || new Date(Date.now() + defaultDurationMs(mode)).toISOString(),
      impersonationSessionId,
      adminId,
      previewEmployeeId: employee?.id ?? null,
      returnUrl: returnUrl || (isReadOnly ? VIEW_AS_EMPLOYEES_RETURN_PATH : ""),
    })
  );
}

export function clearImpersonationStorage() {
  [
    "token",
    "user",
    "currentAccountID",
    "accessPermission",
    "currentAccountDetails",
    "referenceCurrency",
    IMPERSONATION_SESSION_KEY,
  ].forEach((key) => localStorage.removeItem(key));
  clearAdminRestoreStash();
}

export function getImpersonationRemainingLabel(expiresAt) {
  const ms = Math.max(0, Date.parse(expiresAt || "") - Date.now());
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

const VIEW_AS_SUPPORTING_TEXT =
  "When you are viewing as another employee, you will not be allowed to trigger any actions";
const VIEW_AS_SUSPENDED_SUPPORTING_TEXT =
  "This employee's access is suspended, so what you see may be restricted or empty. You will not be allowed to trigger any actions";
const PREVIEW_SUPPORTING_TEXT =
  "This employee has not been granted access yet. You are seeing the Employee Portal as they would. You will not be allowed to trigger any actions";

function resolveBannerName(session) {
  return session?.impersonatedName || session?.impersonatedUserEmail || "user";
}

/** Suspended / blocked / closed targets use the restricted View as Alert copy. */
function isRestrictedViewAsTarget(session) {
  return NON_ACTIVE_TARGET_STATUSES.includes(String(session?.targetStatus || "").toLowerCase());
}

/**
 * Banner wording:
 * - act_as (§7): "Acting as {name} ({account})[ [status]] — 2h session · {countdown} remaining"
 * - view_as: Untitled UI Alert supporting text (normal vs suspended/blocked)
 * - preview: Untitled UI Alert supporting text
 * Exit / End control is rendered separately by the banner button.
 */
export function getImpersonationBannerText(session) {
  if (!session) return "";

  if (session.mode === MODE_VIEW_AS) {
    return isRestrictedViewAsTarget(session)
      ? VIEW_AS_SUSPENDED_SUPPORTING_TEXT
      : VIEW_AS_SUPPORTING_TEXT;
  }
  if (session.mode === MODE_PREVIEW) {
    return PREVIEW_SUPPORTING_TEXT;
  }

  const name = resolveBannerName(session);
  const status = session.targetStatus ? ` [${session.targetStatus}]` : "";
  const account = session.accountName ? ` (${session.accountName})` : "";
  const remaining = getImpersonationRemainingLabel(session.expiresAt);
  return `Acting as ${name}${account}${status} — 2h session · ${remaining} remaining`;
}

export function getImpersonationBannerButtonText(session) {
  if (!session) return "End Impersonation";
  if (session.mode === MODE_VIEW_AS || session.mode === MODE_PREVIEW) return "Exit";
  return "End Impersonation";
}

export function getImpersonationBannerTitle(session) {
  if (!session) return "Impersonation Active";
  const name = resolveBannerName(session);
  // Hyphen separator matches Figma (" - "), not an em dash.
  if (session.mode === MODE_VIEW_AS) {
    const status = isRestrictedViewAsTarget(session) ? " (Suspended)" : "";
    return `View as ${name}${status} - Read Only`;
  }
  if (session.mode === MODE_PREVIEW) {
    return `Preview as ${name} - Read Only`;
  }
  return "Impersonation Active";
}

export function getViewAsLandingPath(mode, accessPermission) {
  // Preview has no Access — always Your Stuff. View-as uses the impersonated
  // user's real permissions (ER-11088).
  if (mode === MODE_PREVIEW) {
    return "/your-stuff/employee/responsibilities";
  }
  const hasYourStuff =
    accessPermission?.has_employee === true &&
    (accessPermission?.your_stuff_permission === true ||
      accessPermission?.yourstuff_permission === true);
  if (hasYourStuff) {
    return "/your-stuff/employee/responsibilities";
  }
  return "/organization/dashboard";
}

export function resolveAccountAppUrl(configService) {
  const fromConfig = configService?.get?.("accountAppUrl");
  if (fromConfig) return String(fromConfig).replace(/\/$/, "");
  const baseURL = configService?.get?.("baseURL") || "";
  // API host is `api.<env>.exiqtive.com`; the customer app is `account.<env>.exiqtive.com`.
  if (baseURL.includes("api.")) return baseURL.replace("api.", "account.").replace(/\/$/, "");
  return window.location.origin;
}

export function resolveAdminAppUrl(configService) {
  const fromConfig = configService?.get?.("adminAppUrl");
  if (fromConfig) return String(fromConfig).replace(/\/$/, "");
  const baseURL = configService?.get?.("baseURL") || "";
  if (baseURL.includes("api.")) return baseURL.replace("api.", "admin.").replace(/\/$/, "");
  return window.location.origin;
}
